/**
 * Port von `Client/Components/Forms/IwmFormRechnung.razor`, seit REQ-27 als Inhalt einer
 * Schublade. Keine Reiter (die API hat keinen Endpunkt für die Zuordnungen einer Rechnung) —
 * die neun Felder stehen in den zwei Abschnitten aus REQ-27 §2.3.
 */

import { receiptApi } from '@/api/services/receiptApi';
import { createReceipt, type Receipt } from '@/types/entities';
import {
  EntityTypes,
  rechnungArtAufteilungDisplayNames,
  rechnungsartDisplayNames,
  type RechnungArtAufteilung,
  type Rechnungsart,
} from '@/types/enums';

import { DateField } from './fields/DateField';
import { EnumField } from './fields/EnumField';
import { NumericField } from './fields/NumericField';
import { TextField } from './fields/TextField';
import { FormFrame, type FormShell } from './FormFrame';
import { FormSection } from './FormSection';
import { receiptRequiredRules } from './requiredFields';
import { useViewForm, type ViewForm } from './useViewForm';

export interface ReceiptFormProps {
  id: number;
  onSave(id: number): void;
  onCancel(): void;
  /** Hülle um Rumpf und Fußleiste — die Seitenleiste steckt beides in ihre `Drawer`. */
  shell: FormShell;
}

/** Felder je Abschnitt (REQ-27 §2.3) — ein Abschnitt ohne Feld in der View entfällt. */
const RECEIPT_SECTION = [
  'Rechnungsnummer',
  'DL',
  'Rechnungsart',
  'ArtAufteilung',
  'Belegdatum',
  'Buchdatum',
] as const;
const AMOUNT_SECTION = ['GesamtbetragNetto', 'Kurs', 'Wkz'] as const;

function hasAnyField(form: ViewForm<Receipt>, names: readonly (keyof Receipt)[]): boolean {
  return names.some((name) => form.viewField(name) !== null);
}

function buildTitle(id: number, receipt: Receipt): string {
  if (id <= 0) return 'Neue Rechnung';
  const number = receipt.Rechnungsnummer ?? '';
  return number === '' ? 'Rechnung' : `Rechnung ${number}`;
}

export function ReceiptForm(props: ReceiptFormProps) {
  const form = useViewForm<Receipt>({
    id: props.id,
    entity: EntityTypes.Rechnung,
    api: receiptApi,
    createEmpty: createReceipt,
    rules: receiptRequiredRules,
    onSave: (id) => {
      props.onSave(id);
    },
  });

  return (
    <FormFrame
      title={buildTitle(props.id, form.model)}
      shell={props.shell}
      ready={form.ready}
      saving={form.saving}
      isValid={form.isValid}
      showRefresh={props.id > 0}
      onRefresh={form.reload}
      onSave={() => {
        void form.save();
      }}
      onCancel={() => {
        props.onCancel();
      }}
    >
      {hasAnyField(form, RECEIPT_SECTION) ? (
        <FormSection title="Rechnung">
          <TextField form={form} name="Rechnungsnummer" label="Rechnungsnummer" />
          <TextField form={form} name="DL" label="DL" />
          <EnumField<Receipt, Rechnungsart>
            form={form}
            name="Rechnungsart"
            displayNames={rechnungsartDisplayNames}
            label="Rechnungsart"
          />
          <EnumField<Receipt, RechnungArtAufteilung>
            form={form}
            name="ArtAufteilung"
            displayNames={rechnungArtAufteilungDisplayNames}
            label="Art Aufteilung"
          />
          <DateField form={form} name="Belegdatum" label="Belegdatum" />
          <TextField form={form} name="Buchdatum" label="Buchdatum" />
        </FormSection>
      ) : null}
      {hasAnyField(form, AMOUNT_SECTION) ? (
        <FormSection title="Beträge">
          <NumericField
            form={form}
            name="GesamtbetragNetto"
            label="Gesamtbetrag Netto"
            nullable={false}
          />
          <NumericField form={form} name="Kurs" label="Kurs" nullable />
          <TextField form={form} name="Wkz" label="Wkz" />
        </FormSection>
      ) : null}
    </FormFrame>
  );
}

export default ReceiptForm;
